/**
=========================================================
* Trust Cargo - top navigation
=========================================================

* Replaces the left sidenav with a horizontal tab bar so the page keeps its
* full width - which matters here, because most screens in this system are
* wide tables (orders, containers, order details).
*/

import { NavLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Icon from "@mui/material/Icon";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import LogOut from "examples/Navbars/DashboardNavbar/logout";
import NotificationBell from "examples/TopNav/NotificationBell";

function TopNav({ brandName, routes }) {
  const { pathname } = useLocation();
  const activeKey = pathname.replace("/", "");

  // only real destinations become tabs; titles and dividers were sidenav-only
  const tabs = routes.filter(({ type, route }) => type === "collapse" && route);

  return (
    <AppBar
      position="sticky"
      sx={({ palette, functions: { linearGradient, rgba }, boxShadows }) => ({
        top: 0,
        zIndex: 1200,
        backgroundImage: linearGradient(palette.gradients.dark.main, palette.dark.main, 160),
        boxShadow: boxShadows.lg,
        borderBottom: `1px solid ${rgba(palette.primary.main, 0.28)}`,
      })}
    >
      <Toolbar
        sx={{
          minHeight: { xs: "auto", md: 68 },
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: { xs: 1.5, md: 3 },
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* brand */}
        <SuiBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ flexShrink: 0, py: { xs: 1, md: 0 } }}
        >
          <Icon sx={({ palette }) => ({ color: palette.primary.main, fontSize: 26 })}>
            local_shipping
          </Icon>
          <SuiTypography
            variant="button"
            fontWeight="bold"
            color="white"
            sx={{ letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}
          >
            {brandName}
          </SuiTypography>
        </SuiBox>

        {/* tabs - scroll sideways on narrow screens rather than wrapping into
            a second row that pushes the content down */}
        <SuiBox
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            flex: 1,
            overflowX: "auto",
            overflowY: "hidden",
            py: 1,
            "&::-webkit-scrollbar": { height: 0 },
            scrollbarWidth: "none",
          }}
        >
          {tabs.map(({ key, name, route }) => {
            const active = activeKey === key;

            return (
              <SuiBox
                key={key}
                component={NavLink}
                to={route}
                sx={({ palette, functions: { rgba }, borders }) => ({
                  position: "relative",
                  px: 2,
                  py: 1.1,
                  borderRadius: borders.borderRadius.md,
                  whiteSpace: "nowrap",
                  color: active ? palette.primary.main : rgba(palette.white.main, 0.7),
                  backgroundColor: active ? rgba(palette.primary.main, 0.14) : "transparent",
                  transition: "background-color 180ms ease, color 180ms ease",
                  "&:hover": {
                    backgroundColor: rgba(palette.primary.main, active ? 0.18 : 0.08),
                    color: active ? palette.primary.main : palette.white.main,
                  },
                  // gold underline marks the current page
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 12,
                    right: 12,
                    bottom: 2,
                    height: 2,
                    borderRadius: 2,
                    backgroundColor: active ? palette.primary.main : "transparent",
                    transition: "background-color 180ms ease",
                  },
                })}
              >
                <SuiTypography
                  variant="button"
                  fontWeight={active ? "bold" : "medium"}
                  color="inherit"
                  sx={{ fontSize: 13.5 }}
                >
                  {name}
                </SuiTypography>
              </SuiBox>
            );
          })}
        </SuiBox>

        {/* Logout lives here now - it was the only reason the breadcrumb row
            below still existed, and that row was otherwise empty space */}
        <SuiBox
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={({ palette, functions: { rgba } }) => ({
            flexShrink: 0,
            pl: 2,
            borderLeft: `1px solid ${rgba(palette.white.main, 0.14)}`,
            // the shared logout link is styled for a light background
            "& a": {
              color: `${rgba(palette.white.main, 0.75)} !important`,
              margin: "0 !important",
              transition: "color 160ms ease",
            },
            "& a:hover": { color: `${palette.primary.main} !important` },
          })}
        >
          <NotificationBell />
          <LogOut />
        </SuiBox>
      </Toolbar>
    </AppBar>
  );
}

TopNav.defaultProps = {
  brandName: "Trust Cargo",
};

TopNav.propTypes = {
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default TopNav;
